import {
  BaseListProps,
  BaseListIconColumnProps,
  BaseListColumnProps,
  BaseItemType,
} from '../../types';
import { getCellHelpPopup, getCellIcon } from '../../utils/utils';
import { isIconColumn } from '../../utils/typeguards';
import { TablePosition } from '../../../../Table';
import { createUseTextLocalizer } from '../../../../../locale/createUseTextLocalizer';
import bundle from '../../../NovaComponents-List-gui-icu.properties';

const useTextLocalizer = createUseTextLocalizer(bundle);

/**
 * @internal
 */
export interface UseSayCellProps {
  columns: Array<BaseListColumnProps | BaseListIconColumnProps>;
  startColIndex: number;
  getItemType: (rowIndex: number) => BaseItemType;
  getRowKey: BaseListProps['getRowKey'];
  getItemGroup?: (rowIndex: number) => string | undefined;
  disabled?: BaseListProps['disabled'];
  isItemDisabled?: BaseListProps['isItemDisabled'];
  narration?: BaseListProps['narration'];
  readOnly?: BaseListProps['readOnly'];
  isItemReadOnly?: BaseListProps['isItemReadOnly'];
}

/**
 * @internal
 */
export const useSayCell = (props: UseSayCellProps) => {
  const {
    columns,
    startColIndex: startIndex,
    getItemType,
    getRowKey,
    getItemGroup,
    disabled,
    isItemDisabled,
    narration,
    readOnly,
    isItemReadOnly,
  } = props;
  const localize = useTextLocalizer();

  return (params: {
    current: TablePosition;
    previous: TablePosition | undefined;
    default: string | null;
  }) => {
    const colIndex = params.current.colIndex - startIndex;
    const rowIndex = params.current.rowIndex ?? -1;
    const prevRowIndex = params.previous?.rowIndex ?? -1;
    const column = columns[colIndex];
    const prevItemType = params.previous ? getItemType(prevRowIndex) : '';
    const itemType = getItemType(rowIndex);
    const isSelectionCell = colIndex < 0;
    const rowKey = getRowKey?.(rowIndex);
    const readOnlyRow = !!readOnly || !!(rowKey && isItemReadOnly?.(rowKey));
    const disabledRow = !!disabled || !!(rowKey && isItemDisabled?.(rowKey));
    const parts =
      (itemType === 'groupItem' ||
        ((itemType === 'item' || itemType === 'linkItem') && !isSelectionCell)) &&
      params.default
        ? [params.default]
        : [];

    // we entered from outside the list, so announce the group if there is one
    if (prevRowIndex === -1) {
      const groupName = getItemGroup?.(rowIndex);
      if (groupName) {
        parts.unshift(makeGroupSayCell(localize));
        parts.unshift(groupName);
      }
    }
    // we entered a new group from below, so announce the new group and 'group start'
    else if (prevRowIndex > rowIndex && prevItemType === 'groupItem' && itemType !== 'groupItem') {
      parts.unshift(makeGroupStartSayCell(localize));
      parts.unshift(getItemGroup?.(rowIndex) ?? '');
    }
    if (itemType === 'groupItem') {
      // announce that this is a group header
      parts.push(makeGroupHeaderSayCell(localize));
      // if we get to this group header from below, don't announce 'group start'
      // because you're already in that group
      if (prevRowIndex !== -1 && prevRowIndex < rowIndex)
        parts.push(makeGroupStartSayCell(localize));
    } else if (column && isIconColumn(column)) {
      const cellellHelpPopup = getCellHelpPopup(column, rowIndex);
      const cellIcon = getCellIcon(column, rowIndex);
      if (cellellHelpPopup) {
        if (!readOnlyRow || (readOnlyRow && column.displayHelpPopupReadOnly)) {
          // button role
          parts.push(
            makeItemButtonIconSayCell(
              localize,
              !!disabledRow || !!(column as BaseListIconColumnProps).disabled?.(rowIndex)
            )
          );
        }
      } else if (cellIcon) {
        if (!!column.onPress) {
          if (!readOnlyRow) {
            // button role
            parts.push(
              makeItemButtonIconSayCell(
                localize,
                !!disabledRow || !!(column as BaseListIconColumnProps).disabled?.(rowIndex)
              )
            );
          }
        } else {
          // graphic role
          parts.push(makeItemStaticIconSayCell(localize));
        }
      }
    } else if (itemType === 'linkItem' && !readOnly) {
      // a role
      parts.push(makeLinkItemSayCell(localize));
    }

    const text = parts.join(', ');

    // give consumer an option to append or modify announcement
    if (typeof narration?.onSayCell === 'function') {
      return narration.onSayCell({
        current: params.current,
        previous: params.previous,
        default: text,
      });
    }

    return text;
  };
};

/**
 * @internal
 */
const makeItemButtonIconSayCell = (localize: (bundle: string) => string, disabled: boolean) => {
  // a role
  const parts = [localize('icon.button.aria')];

  if (!disabled) {
    // a keyboard action
    parts.push(localize('icon.buttonToActivate.aria'));
  }
  return parts.join(', ');
};

/**
 * @internal
 */
const makeItemStaticIconSayCell = (localize: (bundle: string) => string) =>
  localize('icon.graphic.aria');

/**
 * @internal
 */
const makeLinkItemSayCell = (localize: (bundle: string) => string) => {
  return localize('linkItem.aria');
};

/**
 * @internal
 */
const makeGroupSayCell = (localize: (bundle: string) => string) => {
  return localize('group.aria');
};

/**
 * @internal
 */
const makeGroupHeaderSayCell = (localize: (bundle: string) => string) => {
  return localize('groupHeader.aria');
};

/**
 * @internal
 */
const makeGroupStartSayCell = (localize: (bundle: string) => string) => {
  return localize('groupStart.aria');
};
